import { useState, useEffect, useCallback } from 'react';

import {
    getStatusColors,
    updateStatusColor,
    type StatusColorDto,
} from '@/api/client';
import { applyStatusPalette } from '@/lib/statusPalette';

interface Props {
    getToken: () => Promise<string>;
}

/**
 * STATUS COLOURS admin (dock.StatusColor, migration 135).
 *
 * Retunes the appointment palette without a code change or a deploy — arch doc
 * principle 7. Two swatches per status because the two surfaces need different
 * shades of the same meaning: the scheduler is a light page, the dockview board
 * is an always-on dark TV read from 15-40 ft.
 *
 * Saving applies the new palette to the CURRENT page immediately (the same
 * applyStatusPalette the app uses at startup), so the admin sees the calendar
 * behind the dialog retint as they save rather than guessing from a swatch.
 *
 * The set of statuses is fixed by the migration: this screen edits colours, not
 * the vocabulary. Adding a status stays a deliberate migration — Dock has six,
 * and the board's two extra rows are display states, not appointment statuses.
 */
export default function StatusColorsAdmin({ getToken }: Props) {
    const [rows, setRows] = useState<StatusColorDto[]>([]);
    const [draft, setDraft] = useState<Record<string, { scheduler: string | null; board: string | null }>>({});
    const [loading, setLoading] = useState(true);
    const [savingCode, setSavingCode] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [saved, setSaved] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const { colors } = await getStatusColors();
            setRows(colors);
            const d: Record<string, { scheduler: string | null; board: string | null }> = {};
            for (const c of colors) d[c.statusCode] = { scheduler: c.schedulerHex, board: c.boardHex };
            setDraft(d);
        } catch {
            setError('Could not load the status palette.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { void load(); }, [load]);

    const dirty = (c: StatusColorDto): boolean => {
        const d = draft[c.statusCode];
        return !!d && (d.scheduler !== c.schedulerHex || d.board !== c.boardHex);
    };

    async function save(c: StatusColorDto) {
        const d = draft[c.statusCode];
        if (!d) return;
        setSavingCode(c.statusCode);
        setError(null);
        setSaved(null);
        try {
            const token = await getToken();
            const { colors } = await updateStatusColor(token, c.statusCode, d.scheduler, d.board);
            setRows(colors);
            // Repaint everything behind the dialog with the saved palette.
            applyStatusPalette(colors);
            setSaved(c.displayName);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Save failed.');
        } finally {
            setSavingCode(null);
        }
    }

    function setDraftHex(code: string, which: 'scheduler' | 'board', hex: string) {
        setDraft((prev) => ({ ...prev, [code]: { ...prev[code], [which]: hex } }));
    }

    if (loading) return <div className="text-sm text-muted-foreground">Loading palette…</div>;

    return (
        <div className="space-y-4">
            <div>
                <h3 className="text-base font-medium">Status colors</h3>
                <p className="text-sm text-muted-foreground mt-1">
                    Used by the calendar, the carrier portal and the dock board. The board keeps its own
                    shade of each color because it&apos;s a dark screen read from across the warehouse —
                    a color tuned for a white page washes out on a TV. Changes take effect immediately;
                    a board picks them up on its next refresh.
                </p>
            </div>

            {error && (
                <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                    {error}
                </div>
            )}
            {saved && !error && (
                <div className="rounded-md border border-emerald-600/40 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
                    Saved {saved}.
                </div>
            )}

            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                            <th className="py-2 pr-4 font-medium">Status</th>
                            <th className="py-2 pr-4 font-medium">Scheduler</th>
                            <th className="py-2 pr-4 font-medium">Dock board</th>
                            <th className="py-2 pr-4 font-medium" />
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((c) => {
                            const d = draft[c.statusCode] ?? { scheduler: c.schedulerHex, board: c.boardHex };
                            const busy = savingCode === c.statusCode;
                            return (
                                <tr key={c.statusCode} className="border-b last:border-0">
                                    <td className="py-2 pr-4">
                                        <div className="font-medium">{c.displayName}</div>
                                        <div className="text-xs text-muted-foreground">
                                            {c.statusCode}
                                            {c.isBoardOnly && ' · board only'}
                                        </div>
                                    </td>

                                    {/* A NULL hex means the surface never renders this status —
                                        requested/departed/no-show/cancelled never reach the board. */}
                                    <td className="py-2 pr-4">
                                        {d.scheduler === null ? (
                                            <span className="text-xs text-muted-foreground">not shown</span>
                                        ) : (
                                            <div className="flex items-center gap-2">
                                                <input
                                                    type="color"
                                                    value={d.scheduler}
                                                    onChange={(e) => setDraftHex(c.statusCode, 'scheduler', e.target.value)}
                                                    className="h-8 w-12 cursor-pointer rounded border bg-transparent p-0.5"
                                                    aria-label={`${c.displayName} scheduler color`}
                                                />
                                                <code className="text-xs text-muted-foreground">{d.scheduler}</code>
                                            </div>
                                        )}
                                    </td>

                                    <td className="py-2 pr-4">
                                        {d.board === null ? (
                                            <span className="text-xs text-muted-foreground">not shown</span>
                                        ) : (
                                            <div className="flex items-center gap-2">
                                                <input
                                                    type="color"
                                                    value={d.board}
                                                    onChange={(e) => setDraftHex(c.statusCode, 'board', e.target.value)}
                                                    className="h-8 w-12 cursor-pointer rounded border bg-transparent p-0.5"
                                                    aria-label={`${c.displayName} board color`}
                                                />
                                                {/* Preview on the board's actual background, not the
                                                    dialog's white — contrast there is the point. */}
                                                <span
                                                    className="rounded px-2 py-0.5 text-xs"
                                                    style={{ background: '#0e131c', color: d.board }}
                                                >
                                                    on TV
                                                </span>
                                            </div>
                                        )}
                                    </td>

                                    <td className="py-2 pr-4 text-right">
                                        <button
                                            type="button"
                                            disabled={!dirty(c) || busy}
                                            onClick={() => void save(c)}
                                            className="rounded-md border px-3 py-1 text-xs font-medium disabled:opacity-40"
                                        >
                                            {busy ? 'Saving…' : 'Save'}
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <p className="text-xs text-muted-foreground">
                Which statuses exist is fixed in the database — this screen changes their colors, not
                the list. Dock has six appointment statuses; “worked” and “waiting for door” are board
                display states derived from labor, not statuses.
            </p>
        </div>
    );
}
